/**
 * Game state store using Zustand
 */

import { create } from 'zustand';
import type { FactionId } from '@/types';
import type { DifficultyLevel } from '@/game/ai/DifficultySettings';

export type GamePhase = 'menu' | 'playing' | 'paused' | 'victory' | 'defeat';

export interface GameState {
  // Game phase
  phase: GamePhase;
  setPhase: (phase: GamePhase) => void;

  // Time
  gameTime: number;
  gameSpeed: number;
  setGameTime: (time: number) => void;
  setGameSpeed: (speed: number) => void;

  // Player
  playerFaction: FactionId | null;
  playerGold: number;
  setPlayerFaction: (faction: FactionId) => void;
  setPlayerGold: (gold: number) => void;

  // Selection
  selectedEntity: number | null;
  selectedEntities: number[];
  selectedBuildingType: string | null;
  selectedBuildingEntity: number | null; // Selected building for demolition
  selectedBuildingName: string | null;
  setSelectedEntity: (entity: number | null) => void;
  setSelectedEntities: (entities: number[]) => void;
  setSelectedBuildingType: (type: string | null) => void;
  setSelectedBuilding: (entity: number | null, name: string | null) => void;

  // UI state
  showBuildMenu: boolean;
  showFactionPanel: boolean;
  highlightedFaction: FactionId | null;
  setShowBuildMenu: (show: boolean) => void;
  setShowFactionPanel: (show: boolean) => void;
  setHighlightedFaction: (faction: FactionId | null) => void;

  // Stats
  unitCount: number;
  buildingCount: number;
  territoryCount: number;
  setStats: (units: number, buildings: number, territory: number) => void;

  // Messages
  messages: Array<{ id: number; text: string }>;
  addMessage: (message: string) => void;
  clearMessages: () => void;

  // Winner
  winner: FactionId | null;
  setWinner: (faction: FactionId | null) => void;

  // Difficulty
  difficulty: DifficultyLevel;
  setDifficulty: (difficulty: DifficultyLevel) => void;

  // Reset
  reset: () => void;
}

const initialState = {
  phase: 'menu' as GamePhase,
  gameTime: 0,
  gameSpeed: 1,
  playerFaction: null as FactionId | null,
  playerGold: 500,
  selectedEntity: null as number | null,
  selectedEntities: [] as number[],
  selectedBuildingType: null as string | null,
  selectedBuildingEntity: null as number | null,
  selectedBuildingName: null as string | null,
  showBuildMenu: false,
  showFactionPanel: false,
  highlightedFaction: null as FactionId | null,
  unitCount: 0,
  buildingCount: 0,
  territoryCount: 0,
  messages: [] as Array<{ id: number; text: string }>,
  winner: null as FactionId | null,
  difficulty: 'normal' as DifficultyLevel,
};

export const useGameStore = create<GameState>((set) => ({
  ...initialState,

  setPhase: (phase) => set({ phase }),
  setGameTime: (gameTime) => set({ gameTime }),
  setGameSpeed: (gameSpeed) => set({ gameSpeed }),
  setPlayerFaction: (playerFaction) => set({ playerFaction }),
  setPlayerGold: (playerGold) => set({ playerGold }),
  setSelectedEntity: (selectedEntity) => set({ selectedEntity }),
  setSelectedEntities: (selectedEntities) => set({ selectedEntities }),
  setSelectedBuildingType: (selectedBuildingType) => set({ selectedBuildingType }),
  setSelectedBuilding: (selectedBuildingEntity, selectedBuildingName) => set({ selectedBuildingEntity, selectedBuildingName }),
  setShowBuildMenu: (showBuildMenu) => set({ showBuildMenu }),
  setShowFactionPanel: (showFactionPanel) => set({ showFactionPanel }),
  setHighlightedFaction: (highlightedFaction) => set({ highlightedFaction }),
  setStats: (unitCount, buildingCount, territoryCount) => set({ unitCount, buildingCount, territoryCount }),
  addMessage: (message) => {
    const id = Date.now() + Math.random();
    set((state) => ({
      messages: [...state.messages.slice(-4), { id, text: message }], // Keep last 5 messages
    }));
    // Auto-clear this message after 3 seconds
    setTimeout(() => {
      set((state) => ({
        messages: state.messages.filter(m => m.id !== id),
      }));
    }, 3000);
  },
  clearMessages: () => set({ messages: [] }),
  setWinner: (winner) => set({ winner }),
  setDifficulty: (difficulty) => set({ difficulty }),

  reset: () => set(initialState),
}));
